import { AxiosError } from "axios";
import {
    useQuery,
    useMutation,
} from "@tanstack/react-query";
import { Service, ServiceDependencyGroup } from "@/types";
import { ApiResponse } from "@/types/fetch.type";
import axios from "@/axios.config";

interface AllServicesParams {
    enabled?: boolean;
    excludeRenewals?: boolean;
}
export const useGetAllServices = ({ enabled = true, excludeRenewals = false }: AllServicesParams) => {
    return useQuery<ApiResponse<Service[]>, AxiosError>({
        queryKey: ["services", excludeRenewals],
        queryFn: async () => {
            const params = excludeRenewals ? "?excludeRenewals=true" : "";
            const response = await axios.get<ApiResponse<Service[]>>(`/services${params}`);
            return response.data;
        },
        enabled: enabled,
    });
}

export const useCreateService = () => {
    return useMutation<
        ApiResponse<Service>, 
        AxiosError<{ message: string }>, 
        Omit<Service, "id" | "dependentServices"> & { 
            dependencyGroups?: ServiceDependencyGroup[];
            dependentServiceIds?: string[]; // Legacy support
        }
    >({
        mutationFn: async (service) => {
            const response = await axios.post<ApiResponse<Service>>("/services", service);
            return response.data;
        },
    });
}


interface ServicesByCouncilParams {
    enabled?: boolean;
    councilId?: string;
    excludeRenewals?: boolean;
}

export const useGetServicesByCouncil = ({ councilId, enabled = true, excludeRenewals = false }: ServicesByCouncilParams) => {
    return useQuery<ApiResponse<Service[]>, AxiosError>({
        queryKey: ["services", councilId, excludeRenewals],
        queryFn: async () => {
            const params = excludeRenewals ? "?excludeRenewals=true" : "";
            const response = await axios.get<ApiResponse<Service[]>>(`/services/council/${councilId}${params}`);
            return response.data;
        },
        enabled: enabled && !!councilId,
        staleTime: 5 * 60 * 1000, // 5 minutes - prevent unnecessary refetches
        refetchOnWindowFocus: false, // Prevent refetching when window regains focus
        refetchOnMount: false, // Prevent refetching on component mount if data exists
        gcTime: 10 * 60 * 1000, // 10 minutes - keep data in cache longer
        placeholderData: (previousData) => previousData, // Keep previous data during refetches to prevent flickering
    });
}

export const useGetServiceById = (serviceId: string) => {
    return useQuery<ApiResponse<Service>, AxiosError>({
        queryKey: ["services", serviceId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<Service>>(`/services/${serviceId}`);
            return response.data;
        },
    });
}

export const useUpdateService = () => {
    return useMutation<
        ApiResponse<Service>, 
        AxiosError<{ message: string }>, 
        Omit<Service, "dependentServices"> & {
            dependencyGroups?: ServiceDependencyGroup[];
            dependentServiceIds?: string[]; // Legacy support
        }
    >({
        mutationFn: async (service) => {
            const response = await axios.put<ApiResponse<Service>>(`/services/${service.id}`, service);
            return response.data;
        },
    });
}

export const useDeleteService = () => {
    return useMutation<ApiResponse<Service>, AxiosError<{ message: string }>, string>({
        mutationFn: async (serviceId) => {
            const response = await axios.delete<ApiResponse<Service>>(`/services/${serviceId}`);
            return response.data;
        },
    });
}